import {
  createRouterMiddleware,
  createRouterReducer,
  ReduxRouterState,
} from '@lagunovsky/redux-react-router';
import {
  Action,
  AnyAction,
  AsyncThunk,
  AsyncThunkOptions,
  AsyncThunkPayloadCreator,
  combineReducers,
  configureStore,
  Reducer,
  ThunkAction,
} from '@reduxjs/toolkit';
import { createBrowserHistory } from 'history';
import cartReducer from '../reducers/cartSlice';
import dialogReducer from '../reducers/dialogSlice';
import errorReducer from '../reducers/errorSlice';
import languageReducer from '../reducers/languageSlice';
import menuReducer from '../reducers/menuSlice';
import messagesReducer from '../reducers/messagingSlice';
import orderReducer from '../reducers/orderSlice';
import restaurantReducer from '../reducers/restaurantSlice';
import userReducer from '../reducers/userSlice';
import aiReducer from '../redux/features/ai/ai.slice';
import cachedMenuReducer from '../redux/features/cachedMenu/cachedMenu.slice';
import configReducer from '../redux/features/config/config.slice';
import healthStatusCheckReducer from '../redux/features/healthStatusCheck/healthStatusCheck.slice';
import loadingReducer from '../redux/features/loading/loading.slice';
import taskRouterReducer from '../redux/features/taskRouter/taskRouter.slice';
import sessionBoundaryReducer from '../redux/features/sessionBoundary/sessionBoundary.slice';
import autoComboReducer from '../redux/features/autoCombo/autoCombo.slice';
import agentFirstCartEditMiddleware from './agentFirstCartEdit.middleware';
import { messagingTransformMiddleware, socketMiddleware } from './middleware';
import appContainer from '../utils/container';
import menuUnificationReducer from '../redux/features/menuUnification/menuUnification.slice';
import healthScoreReducer from '../redux/features/healthScore/healthScore.slice';
import unresolvedItemsReducer from '../redux/features/unresolvedItems/unresolvedItems.slice';
import aiSuggestionsReducer from '../redux/features/aiSuggestions/aiSuggestions.slice';
import issueReportReducer from '../redux/features/issueReport/issueReport.slice';
import assistRequestReducer from '../redux/features/assistRequest/assistRequest.slice';
import agentModeReducer from '../redux/features/agentMode/agentMode.slice';
import draftCartReducer from '../redux/features/draftCart/draftCart.slice';
import experimentsReducer from '../redux/features/experiments/experiments.slice';
import experimentsMiddleware from '../redux/features/experiments/experiments.middleware';
import { registerLazyContextProvider } from '../utils/lazyOptionBuild';
import { convertToMapUsingName } from '../utils';
import { getRestaurantGroups } from '../utils/menuUnification.utils';

const history = createBrowserHistory();
const appReducer = combineReducers({
  router: createRouterReducer(history) as Reducer<ReduxRouterState, AnyAction>,
  loading: loadingReducer,
  messages: messagesReducer,
  menu: menuReducer,
  cachedMenu: cachedMenuReducer,
  cart: cartReducer,
  ai: aiReducer,
  user: userReducer,
  restaurant: restaurantReducer,
  error: errorReducer,
  order: orderReducer,
  dialog: dialogReducer,
  config: configReducer,
  healthStatusCheck: healthStatusCheckReducer,
  taskRouter: taskRouterReducer,
  sessionBoundary: sessionBoundaryReducer,
  autoCombo: autoComboReducer,
  menuUnification: menuUnificationReducer,
  healthScore: healthScoreReducer,
  language: languageReducer,
  unresolvedItems: unresolvedItemsReducer,
  aiSuggestions: aiSuggestionsReducer,
  experiments: experimentsReducer,
  // HITL-1604: the supervising agent's mode. Reset to Shadow at every
  // session start by the slice itself — deliberately not persisted.
  agentMode: agentModeReducer,
  // HITL-1611: the agent's own cart, outside `cart` so the AI's hypothesis
  // rebuild (clearCart + replay on every changed frame) cannot wipe it.
  // Clears at session start, never on car_exit — see the slice.
  draftCart: draftCartReducer,
  // HITL-1492: deliberately NOT reset on session teardown — HITL-1501's unclassified
  // chip and the pre-lobby panel need reports to outlive the session. The only reset
  // is the `user/logout` wipe below, which is shift-end.
  issueReport: issueReportReducer,
  // HITL-1770: lobby assist requests (HITL 8.0.0). Lobby-only: the session
  // console never reads it. Reset only by the `user/logout` wipe below.
  assistRequest: assistRequestReducer,
});

const rootReducer = (state: any, action: Action) => {
  if (action.type === 'user/logout') {
    // reset all state except user state when logout action is dispatched
    const { user, ...rest } = state;
    const newState: any = {};
    Object.keys(state).forEach((key) => {
      if (key in rest) {
        newState[key] = undefined;
      }
    });

    state = {
      ...newState,
      user,
    };
  }
  return appReducer(state, action);
};

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({ serializableCheck: false }).concat([
      createRouterMiddleware(history),
      // HITL-1646 — before the transform, so the metric it dispatches is picked
      // up and stamped by it like any other outbound event.
      agentFirstCartEditMiddleware,
      experimentsMiddleware,
      messagingTransformMiddleware,
      socketMiddleware,
    ]),
});

// HITL-1748: if a lazily built option outlives its build context (evicted, or
// the tab never registered it), rebuild the context from the current menu.
registerLazyContextProvider((buildViaAutoCombo) => {
  const { menu, restaurant } = store.getState();
  if (!menu.brandMenu) return undefined;
  const { restaurantCode = '', primaryRestaurantCode = '' } =
    restaurant.selectedRestaurantDetails || {};
  return {
    brandMenuItems: menu.brandMenuItemsByName,
    brandMenuConstraints: convertToMapUsingName(menu.brandMenu.constraints),
    unavailableItemsByName: menu.unavailableItemsByName,
    buildViaAutoCombo,
    timezone: restaurant.selectedRestaurantDetails?.timezone,
    restaurantMealPeriodsByName: menu.restaurantMealPeriodsByName,
    // Derived exactly as buildFullMenuUnifiedItem derives it (not read from
    // menu state, which a menu reset empties while keeping the brand menu).
    currentRestaurantGroups: getRestaurantGroups(
      primaryRestaurantCode || restaurantCode,
      menu.brandMenu.restaurant_groups
    ),
    mealPeriodActiveCache: new Map(),
  };
});

appContainer.set('store', store);

export type AppDispatch = typeof store.dispatch;
export type RootStore = { getState: () => RootState; dispatch: AppDispatch };
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;

/**
 * We are augmenting the redux's type definitions here instead of creating a *.d.ts file
 * because we need to access `RootState` and `AppDispatch` types.
 *
 * Notice `ThunkApiConfig` type is used as default type for thunkAPI (the third argument
 * of AsyncThunkPayloadCreator). It will help in destructuring thunkAPI with proper type
 * support for `getStore()` and `dispatch()` functions.
 */

declare module '@reduxjs/toolkit' {
  type ThunkApiConfig = {
    state: RootState;
    dispatch: AppDispatch;
  };

  export function createAsyncThunk<Returned, ThunkArg = void>(
    typePrefix: string,
    payloadCreator: AsyncThunkPayloadCreator<
      Returned,
      ThunkArg,
      ThunkApiConfig
    >,
    options?: AsyncThunkOptions<ThunkArg, ThunkApiConfig>
  ): AsyncThunk<Returned, ThunkArg, ThunkApiConfig>;
}
